import { Component, OnInit, ViewChild, ElementRef, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import chatbotValidation from '../../../assets/static/chatbot-validation.json';
import * as HybridAction from '../../store/actions/hybrid/hybrid.actions';
import * as HybridSelector from '../../store/selectors/hybrid/hybrid.selectors';
import * as SignupAction from '../../store/actions/signup/signup.actions';
import * as SignupSelector from '../../store/selectors/signup/signup.selectors';
import { JwtHelperService } from '@auth0/angular-jwt';
import { FileService } from '../../../../src/app/file.service';
import { CHAT_FORM_CONSTANTS, COUNTRIES_LIST, FILE_MODULE, FOLDER_CONSTANTS, MAX_FILE_SIZE, PATTERN } from '../../../../src/app/constant/app.constant';
import { Location } from '@angular/common';
import { ChatbotFormService } from '../../services/chatbot-form.service';
import { TranslateService } from '@ngx-translate/core';
import { AutoComplete } from 'primeng/autocomplete';
import { environment } from 'projects/nga-PAP/src/environments/environment';
import { ToastrService } from 'projects/ngp-docquity-ds/src/lib/toastr/services/toastr.service';
import { Drugs, Program } from '../../models/sign-up-chatbot.model';

/**
 * Component for handling multi-step patient enrollment form
 * Manages form navigation, validation, file uploads, and state management
 */
@Component({
  selector: 'app-chatform',
  templateUrl: './chatform.component.html',
  styleUrls: ['./chatform.component.scss']
})
export class ChatformComponent implements OnInit, OnDestroy {
  @Input() chatbotConfig: any;
  orderForm: FormGroup;
  patientDetailsForm: FormGroup;
  patientIdForm: FormGroup;
  consentForm: FormGroup;
  orderDetailsForm: FormGroup;
  currentStep: string = 'selectDrug';
  idCardFileError: string | null = null;
  idCardFileName: string | null = null;
  idCardImageSrc: string | null = null;
  idUrl: string | null = null;
  @ViewChild('fileInput', {static : false}) fileInput: ElementRef;
  cardId: string;
  currentPosition: number = 1;
  totalSteps: number = 0;
  consentAccepted: boolean = false;
  programList: any[] = [];
  public drugList: any[] = [];
  public clinicList: any[] = [];
  public quantityList: any[] = [];
  private drugMappings: any[] = [];
  private programSub: Subscription;
  private clinicListSub: Subscription;
  private allowOrderStatusSub: Subscription;
  public patientToken = '';
  public fileTypes = ['image/jpeg', 'image/png'];
  public isFileTypeValid = true;
  public isFileSizeValid = true;
  countries = [
    { name: 'Indonesia', dialCode: '+62', flag: 'assets/images/indonesia-signup-icon.png' },
    // Add more countries as needed
  ];
  selectedCountryFlag = this.countries[0].flag;
  showAllTerms = false;
  public today = new Date();
  showSuccess = false;
  private popStateListener: any;
  @ViewChild('autoCompleteProgram') autoCompleteProgram: AutoComplete;
  
  termsAndConditions: any;
  terms : any[] =[];
  tncObject: any;
  mandatoryConditions: any;

  papId: any;

  private patientauthSub: Subscription;
  private readonly PATIENT_DETAILS_FORM_NAMES = [CHAT_FORM_CONSTANTS.PATIENT_DETAILS, CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS];

  public card: any | null = null;
  public showForm: boolean = true;
  public currentCardForm: FormGroup;
  public chatFormConstants = CHAT_FORM_CONSTANTS;
  private globalErrorSub: Subscription;
  private profileSubmitResponseSub: Subscription;
  private patientOrderResponseSub: Subscription;
  public orderPreview: boolean = false;
  public orderSuccess: boolean = false;
  public isLoadingData: boolean = false;
  public orderSuccessData: any;
  public submittedOrder: boolean = false;
  public selectedQuantity: any;

  pharmaTagline: string;
  pharmaLogo: string | null = null;

  constructor(
    private fb: FormBuilder, 
    private router: Router, 
    private route: ActivatedRoute,
    private store: Store,
    public fileService: FileService,
    private location: Location,
    private chatbotFormService: ChatbotFormService,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.errorSubscription();
    this.profileSubmitResponseSubscription();
    if(localStorage.getItem(CHAT_FORM_CONSTANTS.PHARMA_TAGLINE)){
      this.pharmaTagline = localStorage.getItem(CHAT_FORM_CONSTANTS.PHARMA_TAGLINE);
    }
    this.pharmaLogo = sessionStorage.getItem(CHAT_FORM_CONSTANTS.PHARMA_LOGO);
    
    // Listen for language changes and reload config
    this.translate.onLangChange.subscribe(() => {
      this.setDynamicConfig();
    });
    this.route.params.subscribe(params => {
      const pageIdParam = params['cardId'];
      const pageNameParam = params['pageName'];
      this.route.url.subscribe(segments => {
        this.setDynamicConfig();
        this.currentStep = segments.length > 1 ? segments[1].path : 'selectDrug';
        const currentCard = this.getCurrentCard(Number.parseInt(pageIdParam),pageNameParam);
        if (currentCard) {
          this.card = currentCard;
          this.currentPosition = currentCard.position;
          this.cardId = currentCard.cardId;
          this.currentCardForm = this.getFormGroup(currentCard.pageName);

          if (currentCard.position === 5) {
            this.isLoadingData = true;
            this.getAllowOrderStatusSubscription();
            this.store.dispatch(HybridAction.getAllowOrderStatus({ consentToken: sessionStorage.getItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN), accessToken: JSON.parse(localStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH))?.accessToken }));
          } else {
            this.isLoadingData = false;
            this.setCurrentCard();
          }
        }
      });
    });

    // Load initial program list
    this.getProgramList();

    this.getProgramListSubscription();
    this.patientOrderResponseSubscription();

    // Disable browser back button
    this.popStateListener = this.location.subscribe(() => {
      this.location.forward();
    });
  }

  private setCurrentCard(): void {
    const savedData = this.chatbotFormService.getFormData(this.card.pageName);

    if (savedData) {
      this.currentCardForm = this.getFormGroup(this.card.pageName);
      this.currentCardForm.patchValue(savedData);
    }
    else {
      this.currentCardForm = this.getFormGroup(this.card.pageName);
      let formValOnReload = this.setDataOnReload(this.card.pageName);
      if (formValOnReload?.dob) {
        formValOnReload['dob'] = new Date(formValOnReload?.dob);
      }
      if (formValOnReload?.dateOfPrescription) {
        formValOnReload['dateOfPrescription'] = new Date(formValOnReload?.dateOfPrescription);
      }
      if (formValOnReload) {
        this.currentCardForm.patchValue(formValOnReload);
      }
      else {
        this.showForm = true;
      }
    }
  }

  private getProgramList(): void {
    this.store.dispatch(HybridAction.loadPrograms({
      keyword: '',
      endPoint: 'api/v1/programs',
      method: 'GET',
      pharma: localStorage.getItem('pharmaId'),
      limit: '100',
      consentToken: this.card?.pageName === CHAT_FORM_CONSTANTS.ORDER_DETAILS ? sessionStorage.getItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN) : this.patientToken ?? ''
    }));
  }

  private getAllowOrderStatusSubscription(): void {
    this.allowOrderStatusSub = this.store.select(HybridSelector.getAllowOrderStatus).subscribe(data => {
      if (data) {
        this.isLoadingData = false;
        if (data?.data) {
          this.setCurrentCard();
        } else {
          this.orderSuccess = true;
          this.orderSuccessData = { data: { orderCondition: false, failureReason: 'PREVIOUS_ORDER_NOT_COMPLETED' } };
          this.store.dispatch(HybridAction.setAllowOrderStatus({ data: null }));
          this.removePatientDetailsLocalStorage();
          localStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH);
        }
      }
    });
  }

  private getProgramListSubscription(): void {
    this.programSub = this.store.select(HybridSelector.getPrograms).subscribe(data => {
      if (data) {
        if(this.card?.pageName === CHAT_FORM_CONSTANTS.ORDER_DETAILS) {
          this.prepareDrugOptionList(data[0]?.Program);
          this.getClinicList(data[0]?.Program?.Id);
        } else {
        this.programList = data;
        if (this.programList.length === 1) {
          const form = this.getFormGroup(CHAT_FORM_CONSTANTS.ORDER);
          if (form) {
            form.patchValue({
              program: this.programList[0]
            });
            form.updateValueAndValidity();
          }
        }}
      }
    });
  }

  private getClinicList(programId: number) {
    const pharmaCode = localStorage.getItem('pharmaCode');
    this.store.dispatch(SignupAction.loadClinics({ pharmaCode: pharmaCode, pharmaId: programId }));
    this.clinicListSub = this.store.select(SignupSelector.getClinicList).subscribe(data => {
      if(data) {
        this.clinicList = data;
      }
    });
  }

  private prepareDrugOptionList(program: Program) {
    this.drugList = [];
    this.drugMappings = JSON.parse(program?.DrugMappings);
    const drugList = program?.Drugs;
    drugList.forEach(drug => {
      const drugStrength = JSON.parse(drug.Strength);
      drugStrength.forEach(strength => {
        this.drugList.push({
          id: drug.Id,
          name: drug.BrandName,
          genericName: drug.GenericName,
          strength: {
            unit: {
              id: 1,
              name: strength.Unit
            },
            value: strength.Value
          },
          dosageForm: drug.DosageForm,
          routeOfAdministration: drug.Route
        })
      });
    });
  }

  public getFieldOptionList(key: string) {
    if('drugs' === key) {
      return this.drugList;
    }
    if('treatmentClinic' === key) {
      return this.clinicList;
    }
    if('quantity' === key) {
      return this.quantityList;
    }
  }

  public onSelectItem(event: any, field: string): void {
    if ('drugs' === field) {
      this.quantityList = [];
      let setQuantity: boolean = false;
      const drugMap = this.drugMappings.find(map => map.Id === event.id);
      if (drugMap.DispenseUnitMultiplier > 0) {
        for(let i = 1; i <= 3; i++) {
          this.quantityList.push({ id: i * drugMap.DispenseUnitMultiplier, name: i * drugMap.DispenseUnitMultiplier });
          if(i * drugMap.DispenseUnitMultiplier === this.selectedQuantity?.id) {
            setQuantity = true;
          }
        }
      } else {
        this.quantityList.push({ id: 0, name: 0 });
      }
      this.currentCardForm.get(['drugs', 0]).patchValue({
        id: event.id,
        name: event.name,
        genericName: event.genericName,
        pap: true,
        strength: event.strength,
        form: event.dosageForm,
        frequency: {
          unit: '',
          value: 0
        },
        duration: {
          unit: '',
          value: 0
        },
        routeOfAdministration: event.routeOfAdministration,
        instruction: '',
        quantity: setQuantity ? this.selectedQuantity?.id : '',
        prescribedDosage: {
          unit: '',
          value: ''
        },
        prescribedFrequency: ''
      })
    }
    if('quantity' === field) {
      this.selectedQuantity = event;
      this.currentCardForm.get(['drugs', 0, 'quantity']).patchValue(event.id);
    }
    if('treatmentClinic' === field) {
      this.currentCardForm.get('treatmentClinic').patchValue(event);
    }
  }

  /**
   * Sets up subscription for global error handling
   * Displays error toast when errors occur
   */
  private errorSubscription(): void {
    this.globalErrorSub = this.store.select(SignupSelector.getChatbotError).subscribe(data => {
      if (data) {
        this.toastr.showError("Error", "Something went wrong");
        this.store.dispatch(SignupAction.resetErrors());
      }
    });
  }

  /**
   * Sets up subscription for profile submission response
   * Handles navigation after successful profile submission
   */
  private profileSubmitResponseSubscription(): void {
    this.profileSubmitResponseSub = this.store.select(SignupSelector.getProfileSubmitResponse).subscribe(data => {
      if (data && data?.status === CHAT_FORM_CONSTANTS.SUCCESS) {
        this.store.dispatch(SignupAction.resetProfileSubmitResponse());
        const nextPosition = this.card.position + 1;
        const nextCard = this.chatbotConfig?.find(c => c.position === nextPosition);
        if (nextCard) {
          this.currentPosition = nextPosition;
          this.currentCardForm = this.getFormGroup(nextCard.pageName);
          this.router.navigate(['enroll', nextCard.cardId, nextCard.pageName]);
          if(nextCard.pageName === CHAT_FORM_CONSTANTS.ORDER_DETAILS) {
            this.getProgramList();
            this.patientOrderResponseSubscription();
          }
        }else {
          this.showSuccess = true;
          this.onSuccessPageLoad();
        }
      }
    });
  }

  private patientOrderResponseSubscription(): void {
    this.patientOrderResponseSub = this.store.select(SignupSelector.getPatientOrderResponse).subscribe(data => {
      if(data) {
        this.orderSuccessData = data.data;
        this.store.dispatch(SignupAction.resetProfileSubmitResponse());
        this.store.dispatch(SignupAction.setPatientOrderResponse({ data: null }));
        this.orderSuccess = true;
        this.submittedOrder = true;
        this.removePatientDetailsLocalStorage();
        localStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH);
      }
    })
  }

  ngOnDestroy(): void {
    if (this.allowOrderStatusSub) {
      this.allowOrderStatusSub.unsubscribe();
    }
    if (this.clinicListSub) {
      this.clinicListSub.unsubscribe();
    }
    if (this.programSub) {
      this.programSub.unsubscribe();
    }
    if (this.popStateListener) {
      this.popStateListener.unsubscribe();
    }
    if (this.patientauthSub) {
      this.patientauthSub.unsubscribe();
    }
    if (this.globalErrorSub) {
      this.globalErrorSub.unsubscribe();
    }
    if(this.profileSubmitResponseSub){
      this.profileSubmitResponseSub.unsubscribe();
    }
    // Clear form data when component is destroyed
    this.chatbotFormService.clearFormData();
    this.removePatientDetailsLocalStorage();
  }

  /**
   * Sets dynamic configuration based on environment and process
   * Updates form validations based on configuration
   */
  setDynamicConfig() {
    const process = localStorage.getItem('process') || 'ID_NOV_NR_PND_1';
    this.chatbotConfig = chatbotValidation[environment.env][process] || chatbotValidation[environment.env]['ID_NOV_NR_PND_1'];
    this.totalSteps = this.chatbotConfig.length;
    this.updateFormValidations();
    this.initiateOrderDetailsForm();
  }

  private getDrugArray(): FormGroup {
    return this.fb.group({
      id: [0, Validators.required],
      name: ['', Validators.required],
      genericName: ['', Validators.required],
      pap: [true],
      strength: this.fb.group({
        unit: this.fb.group({
          id: [1],
          name: ['', Validators.required]
        }),
        value: [0, Validators.required]
      }),
      form: [],
      dosage: [0],
      frequency: this.fb.group({
        unit: [],
        value: []
      }),
      duration: this.fb.group({
        unit: [],
        value: []
      }),
      routeOfAdministration: [],
      instruction: [],
      quantity: ['', Validators.required],
      prescribedDosage: this.fb.group({
        unit: [''],
        value: ['']
      }),
      prescribedFrequency: []
    });
  }

  private initiateOrderDetailsForm(): FormGroup {
    return this.fb.group({
      urls: this.fb.array([]),
      doctorName: [''],
      drugs: this.fb.array([this.getDrugArray()]),
      treatmentHospital: this.fb.group({
        id: [0],
        name: ['', Validators.required]
      }),
      prescriptionNo: [''],
      dateOfPrescription: [0],
      treatmentClinic: this.fb.group({
        id: [0],
        name: ['', Validators.required]
      })
    });
  }

  public getCurrentCard(inputId: number,name: string) {
   return this.chatbotConfig.find(card => card.pageName === name && card.cardId === inputId);
  }

  /**
   * Initializes all form groups with empty controls
   * Sets up initial form structure
   */
  private initializeForms() {
    this.currentCardForm = this.fb.group({});
    this.orderForm = this.fb.group({});
    this.orderDetailsForm = this.initiateOrderDetailsForm();
    this.patientDetailsForm = this.fb.group({});  
    this.patientIdForm = this.fb.group({});
    this.consentForm = this.fb.group({
      termsAccepted: [true]
    });
  }

  /**
   * Updates form validations based on chatbot configuration
   * Sets up validators and default values for each form field
   */
  private updateFormValidations() {
    this.chatbotConfig.forEach(card => {
      if (card.fields) {
        const controls = {};
        card.fields.forEach(field => {
          const validators = [];
          if (field.isRequired) validators.push(Validators.required);
          if (field.pattern) validators.push(Validators.pattern(new RegExp(field.pattern)));
          if (field.maxLength) validators.push(Validators.maxLength(field.maxLength));
          // Set default value for caregiverCountryCode
          if (field.key === 'caregiverCountryCode') {
            controls[field.key] = ['+62', validators];
          } else {
            controls[field.key] = [field.defaultValue || '', validators];
          }
        });

        switch (card.pageName) {
          case CHAT_FORM_CONSTANTS.ORDER:
            this.orderForm = this.fb.group(controls);
            break;
          case CHAT_FORM_CONSTANTS.PATIENT_DETAILS:
            this.patientDetailsForm = this.fb.group(controls);
            // Set the flag to match the default country code
            const defaultCode = this.patientDetailsForm.get('caregiverCountryCode')?.value;
            const found = this.countries.find(c => c.dialCode === defaultCode);
            this.selectedCountryFlag = found ? found.flag : this.countries[0].flag;
            // Subscribe to changes
            this.patientDetailsForm.get('caregiverCountryCode')?.valueChanges.subscribe(code => {
              const found = this.countries.find(c => c.dialCode === code);
              this.selectedCountryFlag = found ? found.flag : this.countries[0].flag;
            });
            break;
          case CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS:
            this.patientIdForm = this.fb.group(controls);
            break;
        }
      }
    });
  }

  /**
   * Checks if a field is invalid and has been touched
   * @param section - The form section name
   * @param fieldKey - The field key to check
   * @returns boolean indicating if field is invalid
   */
  isFieldInvalid(section: string, fieldKey: string): boolean {
    const form = this.currentCardForm;
    const control = form.get(fieldKey);
    return control?.invalid && (control?.dirty || control?.touched);
  }

  private setEpochDate(date: string): number {
    const dateObj = new Date(date);
    const utcDate = new Date(Date.UTC(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate()
    ));
    return Math.floor(utcDate.getTime() / 1000);
  }

  public onViewOrderDetails(): void {
    this.orderSuccess = false;
    this.submittedOrder = true;
    this.orderPreview = true;
  }

  public onEditOrder(): void {
    this.orderPreview = false;
  }

  /**
   * Handles form submission for each card
   * Saves form data and navigates to next step
   * @param card - The current card configuration
   */
  onCardSubmit(card: any): void {
    const form = this.currentCardForm;
    if (form.valid) {
      // Save form data before navigation
      this.chatbotFormService.saveFormData(card.pageName, form.value);
      sessionStorage.setItem(card.pageName,JSON.stringify(form.value));

      const consentTokenVal = sessionStorage.getItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN);
      this.patientToken = consentTokenVal;

      if(card.pageName === CHAT_FORM_CONSTANTS.ORDER_DETAILS && !this.orderPreview) {
        this.orderPreview = true;
        return;
      }

      if (card.pageName === CHAT_FORM_CONSTANTS.PATIENT_DETAILS) {
        const idToken = JSON.parse(localStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH));
        const helper = new JwtHelperService();
        const decodedToken = helper.decodeToken(idToken.accessToken);
        const phone = decodedToken.data.phoneNumber;
        const formData = { ...form.value, phone , formId: 3};
        if (formData.dob) {
          formData.dob = this.setEpochDate(formData.dob) + 1;
        }

        this.store.dispatch(SignupAction.submitFormData({
          endPoint: `api/v1/consent/patient/${this.patientToken}/profile`,
          method: 'PATCH',
          formData: formData,
          applicationType: 'HYBRID',
          idToken: JSON.parse(localStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH))?.accessToken
        }));
        return;
      }
      
      const nextPosition = card.position + 1;
      const nextCard = this.chatbotConfig?.find(c => c.position === nextPosition);
      if (nextCard && nextCard.pageName !== CHAT_FORM_CONSTANTS.ORDER_DETAILS) {
        this.currentPosition = nextPosition;
        this.currentCardForm = this.getFormGroup(nextCard.pageName);
        this.router.navigate(['enroll', nextCard.cardId, nextCard.pageName]);
      } else if (card.pageName === CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS) {
        this.onPatientIdSubmit();
      } else if(card?.pageName === CHAT_FORM_CONSTANTS.ORDER_DETAILS) {
        let formData = this.currentCardForm.value;
        if(formData?.dateOfPrescription) {
          const epochDate = this.setEpochDate(this.currentCardForm?.value?.dateOfPrescription);
          formData.dateOfPrescription = epochDate;
        }
        this.store.dispatch(SignupAction.postPatientOrder({ formData: formData, consentToken: this.patientToken, idToken: JSON.parse(localStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH))?.accessToken }));
      }
    } else {
      Object.keys(form.controls).forEach(key => {
        form.get(key)?.markAsTouched();
      });
    }
  }

  /**
   * Handles patient ID form submission
   * Uploads ID card and submits form data
   */
  onPatientIdSubmit() {
    if (this.card.pageName === CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS && this.currentCardForm.valid) {
      const formData = this.patientIdForm.value;
      formData.formId = 4;
      if (this.idUrl) {
        formData.idUrl = this.idUrl;
        const consentTokenVal = sessionStorage.getItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN);
        this.patientToken = consentTokenVal;
        this.store.dispatch(SignupAction.submitFormData({
          endPoint: `api/v1/consent/patient/${this.patientToken}/id-card`,
          method: 'PATCH',
          formData: formData,
          applicationType: 'HYBRID',
          idToken: JSON.parse(localStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH))?.accessToken
        }));
      }
      
    } else {
      // Mark all form controls as touched to show validation errors
      Object.keys(this.patientIdForm.controls).forEach(key => {
        this.patientIdForm.get(key)?.markAsTouched();
      });
    }
  }

  /**
   * Generates a unique filename for ID card upload
   * @param time - Timestamp for unique filename
   * @returns Formatted filename string
   */
  getFileNameCurrent(time: number): string {
    return `${time}-1-idcard`;
  }

  /**
   * Lifecycle hook called after view initialization
   * Sets up file input reference
   */
  ngAfterViewInit() {
    // Ensure fileInput is properly initialized
    if (this.fileInput) {
    }
  }
 
  /**
   * Handles ID card file change event
   * Validates and uploads file
   * @param event - File input change event
   */
  async onIdCardFileChange(event: any): Promise<void> {
    try {
      if (!event || !event.target) {
        return;
      }
 
      const file = event.target.files[0];
      
      if (file) {
        this.resetIdCardFile();
        // Validate file type
        if (!this.fileTypes.includes(file.type)) {
          this.idCardFileError = 'Only jpeg/png files are allowed';
          this.isFileTypeValid = false;
          return;
        }
        this.isFileTypeValid = true;
        
        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          this.idCardFileError = 'File size must not exceed 20 MB';
          this.isFileSizeValid = false;
          return;
        }
        this.isFileSizeValid = true;
 
        try {
          // Upload file using FileService
          const time = Date.now();
          const fileName = this.getFileNameCurrent(time);
          const countryName = localStorage.getItem('country') || COUNTRIES_LIST.PHILLIPPINS.name;
          const getPresignedUrl = await this.fileService.uploadFile(
            file,
            FOLDER_CONSTANTS.ID_CARD,
            fileName,
            FILE_MODULE.PAP,
            countryName
          );
 
          this.idCardFileName = file.name;
          this.idUrl = getPresignedUrl;
          this.patientIdForm.get('idUrl').setValue(getPresignedUrl);
          this.currentCardForm?.get('idUrl')?.setValue(getPresignedUrl);
          
          // Display preview
          const reader = new FileReader();
          reader.onload = (e: any) => {
            this.idCardImageSrc = e.target.result;
          };
          reader.readAsDataURL(file);
        } catch (error) {
          this.idCardFileError = 'Error uploading file. Please try again.';
          console.error('File upload error:', error);
        }
      }
    } catch (error) {
      console.error('Error in file change handler:', error);
      this.idCardFileError = 'Error processing file. Please try again.';
    }
  }

  /**
   * Removes uploaded ID card file
   * Resets file input and related properties
   */
  removeIdCardFile(): void {
    this.resetIdCardFile();
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * Resets all ID card file related properties
   * Clears file input, preview, and error states
   */
  private resetIdCardFile(): void {
    this.idCardFileName = '';
    this.idCardImageSrc = null;
    this.idUrl = null;
    this.patientIdForm.get('idUrl').setValue(null);
    this.idCardFileError = '';
    this.isFileTypeValid = true;
    this.isFileSizeValid = true;
  }

  /**
   * Handles consent form submission
   * Submits program data and manages navigation
   */
  onConsentSubmit(): void {
    if (this.consentForm.valid) {
      this.consentAccepted = true;
      
      this.store.dispatch(HybridAction.submitProgramData({
        endPoint: 'api/v1/consent/patient/pap',
        method: 'POST',
        formData: {
          pap: this.papId
        },
        applicationType: 'HYBRID',
        accessToken: JSON.parse(localStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH))?.accessToken
      }));

      // Subscribe to consent token response
      this.store.select(HybridSelector.getConsentToken).subscribe(data => {
        if (data !== null) {
          this.patientToken = data.consentToken;
          sessionStorage.setItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN,this.patientToken);
        }
      });

      this.store.select(HybridSelector.getCreds).subscribe(patientCreds =>{
        let patientCredsToSubmit = patientCreds;
        if(patientCredsToSubmit == null){
          patientCredsToSubmit = JSON.parse(sessionStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH));
        }
        if(patientCredsToSubmit !== null){
          const helper = new JwtHelperService();
          const decodedToken = helper.decodeToken(patientCredsToSubmit.accessToken);
          let nextPosition = 0;

          if(decodedToken?.data?.exists == true){
            if(decodedToken?.data?.formId === 0){
              const patientDetailsFormPosition = this.chatbotConfig?.find(c => c.pageName === CHAT_FORM_CONSTANTS.PATIENT_DETAILS)?.position;
              if(patientDetailsFormPosition){
                nextPosition = patientDetailsFormPosition ;
              }
              else{
                /**
                 * Setting Default Patient Details Form Position
                 * Remove if condition is invalid
                 */
                nextPosition = 3;
              }
            }
            else{
              nextPosition = decodedToken?.data?.formId + 1 ;
            }
            const nextCard = this.chatbotConfig?.find(c => c.position === nextPosition);
            if (nextCard) {
              this.router.navigate(['enroll', nextCard.cardId, nextCard.pageName]);
            }
          }
          else{
            const nextPosition = this.currentPosition + 1;
            const nextCard = this.chatbotConfig.find(c => c.position === nextPosition);
            if (nextCard) {
              this.currentPosition = nextPosition;
              this.router.navigate(['enroll', nextCard.cardId, nextCard.pageName]);
            }
  
          }

        }
      })
    }
  }

  /**
   * Returns the appropriate form group based on page name
   * @param pageName - The name of the page/form
   * @returns The corresponding FormGroup
   */
  getFormGroup(pageName: string): FormGroup {
    switch (pageName) {
      case CHAT_FORM_CONSTANTS.ORDER:
        return this.orderForm;
      case CHAT_FORM_CONSTANTS.PATIENT_DETAILS:
        return this.patientDetailsForm;
      case CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS:
        return this.patientIdForm;
      case CHAT_FORM_CONSTANTS.CONSENT:
        return this.consentForm;
      case CHAT_FORM_CONSTANTS.ORDER_DETAILS:
        return this.orderDetailsForm;
      default:
        return this.orderForm;
    }
  }

  /**
   * Handles navigation to previous card
   * Restores form data and manages navigation
   */
  goToPreviousCard(): void {
    if(!this.orderSuccess && this.submittedOrder) {
      this.orderSuccess = true;
      this.submittedOrder = true;
      this.orderPreview = true;
      return;
    }
    const currentCard = this.card;
    if (!currentCard) return;
    // If on the order card, go back to chatbot login page
    if (currentCard.pageName === CHAT_FORM_CONSTANTS.ORDER || currentCard.pageName === CHAT_FORM_CONSTANTS.PATIENT_DETAILS || currentCard.pageName === CHAT_FORM_CONSTANTS.ORDER_DETAILS) {
      this.router.navigate(['/chatbot']);
      localStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH);
      return;
    }
    if(currentCard.pageName === CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS && !sessionStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS)){
      this.removeIdCardFile();
    }
    const prevPosition = currentCard.position - 1;
    const prevCard = this.chatbotConfig.find(card => card.position === prevPosition);
    if (prevCard) {
      // Get saved form data
      const savedData = this.chatbotFormService.getFormData(prevCard.pageName);
      if (savedData) {
        const form = this.getFormGroup(prevCard.pageName);
        form.patchValue(savedData);
      }
      else{
        if(this.validatePageName(prevCard.pageName)){
            const patientConsentCode = sessionStorage.getItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN);
            if(patientConsentCode){
              this.patientToken = patientConsentCode;
              this.store.dispatch(HybridAction.loadHybridForms({ consentToken: this.patientToken , formId : prevCard.cardId}));  

              this.store.select(HybridSelector.getHybridForms).subscribe(data => {
                if(data && data!=null){
                  let val = {};
                  const prevCardData = data?.blocks?.find(block => block.id===prevCard.cardId);
                  if(prevCardData){
                    const form = this.getFormGroup(prevCard.pageName);
                    Object.keys(form.controls).forEach((control) => {
                      let controlValue = prevCardData?.attributes?.find(attribute => attribute?.fieldName === control)?.value;
                        if(!controlValue){
                          controlValue = '';
                        }
                        if(control==='gender' && controlValue==='NA'){
                          controlValue = '';
                        }
                        if(control==='caregiverCountryCode'){
                          controlValue = "+" + controlValue
                        }
                        if(control==='caregiverMobile' && controlValue==="0"){
                          controlValue = '';
                        }
                        if(control==='dob'){
                          // return
                          controlValue = this.convertToISO(controlValue);
                        }
                        val[control] = controlValue;
                      
                    })                
                    form.patchValue(val);
                    sessionStorage.setItem(prevCard.pageName,JSON.stringify(val))
                  }
                }
              })
            }
          
         
        }
      }

      this.currentPosition = prevCard.position;
      this.cardId = prevCard.cardId;
      this.router.navigate(['enroll', prevCard.cardId, prevCard.pageName]);
    }
  }

  /**
   * Handles user input for form fields
   * Applies specific formatting based on field type
   * @param pageName - The current page name
   * @param fieldName - The field being edited
   * @param event - The input event
   */
  userInput(pageName: string, fieldName: string, event: any): void {
    if (event.target.value) {
      const value = event.target.value;
      if (fieldName === 'caregiverMobile') {
        const noSpacesValue = value.replace(/\s/g, '');
        if (noSpacesValue[0] === '0') {
          this.currentCardForm.get(fieldName).setValue(noSpacesValue.substring(1));
        } else {
          this.currentCardForm.get(fieldName).setValue(noSpacesValue);
        }
      } else if (fieldName === 'idCardNumber' || fieldName === 'prescriptionNo') {
        const noSpacesValue = value.replace(/\s/g, '');
        this.currentCardForm.get(fieldName).setValue(noSpacesValue);
      } else if (value[0] === ' ') {
        this.currentCardForm.get(fieldName).setValue(value.trimStart());
      }
    }
  }

  /**
   * Returns translation key for field label
   * @param field - The field configuration
   * @returns Translation key string
   */
  getFieldLabelKey(field: any): string {
    switch (field.key) {
      case 'firstName': return 'PATIENT_FIRST_NAME';
      case 'middleName': return 'PATIENT_MIDDLE_NAME';
      case 'lastName': return 'PATIENT_LAST_NAME';
      case 'dob': return 'PATIENT_DOB';
      case 'gender': return 'PATIENT_GENDER';
      case 'initials': return 'PATIENT_INITIALS_ANY';
      case 'address': return 'PATIENT_ADDRESS';
      case 'caregiverName': return 'CAREGIVER_FULL_NAME';
      case 'caregiverMobile': return 'CAREGIVER_MOBILE_NUMBER';
      case 'caregiverRelation': return 'CAREGIVER_RELATION';
      case 'filledBy': return 'INDICATE_FORM_FILLER';
      case 'idCardType': return 'PATIENT_ID_CARD_TYPE';
      case 'idCardNumber': return 'PATIENT_ID_CARD_NUMBER';
      case 'idUrl': return 'UPLOAD_SUPPORTING_DOCUMENTS';
      default: return field.label;
    }
  }

  /**
   * Returns translation key for field placeholder
   * @param field - The field configuration
   * @returns Translation key string
   */
  getFieldPlaceholderKey(field: any): string {
    switch (field.key) {
      case 'firstName': return 'ENTER_FIRST_NAME';
      case 'middleName': return 'ENTER_MIDDLE_NAME';
      case 'lastName': return 'ENTER_LAST_NAME';
      case 'dob': return 'DD_MM_YYYY'; // or a specific placeholder key if you have one
      case 'gender': return 'SELECT_YOUR_GENDER';
      case 'initials': return 'PATIENT_INITIALS_EXAMPLE';
      case 'address': return 'ENTER_ADDRESS';
      case 'caregiverName': return 'ENTER_CAREGIVER_FULL_NAME';
      case 'caregiverMobile': return 'ENTER_MOBILE_NUMBER';
      case 'caregiverRelation': return 'SELECT_RELATIONSHIP';
      case 'formFilledBy': return 'SELECT_OPTION';
      case 'idCardType': return 'SELECT_ID_CARD_TYPE';
      case 'idCardNumber': return 'ENTER_ID_NUMBER';
      case 'idUrl': return 'UPLOAD_ID_HINT';
      case 'dateOfPrescription': return 'DD_MM_YYYY';
      default: return field.placeholder;
    }
  }

  /**
   * Handles program selection
   * Updates form and loads terms & conditions
   * @param program - The selected program
   */
  onSelectProgram(program: any): void {
    const form = this.getFormGroup('order');
    if (form) {
      form.patchValue({
        program: program
      });
      form.markAsTouched();
      form.updateValueAndValidity();

      // terms and condition addition to the array
      this.terms=[]
      this.termsAndConditions= program?.Program?.PatientConsents;
      this.tncObject = JSON.parse(this.termsAndConditions);
      this.mandatoryConditions= JSON.parse(this.tncObject.Mandatory[0])
      for(let i=0;i<this.mandatoryConditions.General.length;i++){
        this.terms.push(this.mandatoryConditions.General[i])
      }
      this.papId=program.Program.Id;
    }
  }

  /**
   * Prevents spaces in input fields
   * @param pageName - The current page name
   * @param fieldName - The field being edited
   * @param event - The input event
   */
  preventSpaces(pageName: string, fieldName: string, event: any) {
    const input = event.target;
    const value = input.value;
    const noSpacesValue = value.replace(/\s/g, '');
    if (value !== noSpacesValue) {
      input.value = noSpacesValue;
      this.currentCardForm.get(fieldName).setValue(noSpacesValue);
    }
  }

  /**
   * Validates if page name is in patient details form names
   * @param pageName - The page name to validate
   * @returns boolean indicating if page is valid
   */
  validatePageName(pageName: string): boolean {
      return this.PATIENT_DETAILS_FORM_NAMES.includes(pageName);
  }

  /**
   * Converts timestamp to ISO date string
   * @param dateStr - Timestamp string to convert
   * @returns Date object
   */
  convertToISO(dateStr: string): any {
    const timestamp = parseInt(dateStr, 10);
    return new Date(timestamp * 1000);
  }

  /**
   * Removes patient details from local storage
   * Cleans up stored form data
   */
  private removePatientDetailsLocalStorage(): void {
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_DETAILS);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.ORDER);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.PHARMA_LOGO);
    localStorage.removeItem(CHAT_FORM_CONSTANTS.PHARMA_TAGLINE);
  }

  /**
   * Sets form data on page reload
   * Restores saved form state
   * @param pageName - The page to restore data for
   * @returns The restored form data
   */
  private setDataOnReload(pageName: string): any {
    switch(pageName){
      case CHAT_FORM_CONSTANTS.PATIENT_DETAILS:
        const patientDetailsLocalStorage = sessionStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_DETAILS);
        if(patientDetailsLocalStorage){
          return JSON.parse(patientDetailsLocalStorage) ;
        }
        break;
      case CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS:
        const patientIdDetailsLocalStorage = sessionStorage.getItem(CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS);
        if(patientIdDetailsLocalStorage){
          return JSON.parse(patientIdDetailsLocalStorage) ;
        }
        break;
      case CHAT_FORM_CONSTANTS.ORDER:
        const orderLocalStorage = sessionStorage.getItem(CHAT_FORM_CONSTANTS.ORDER);
        if(orderLocalStorage){
          const orderSelectEvent = JSON.parse(orderLocalStorage) ;
          this.onSelectProgram(orderSelectEvent.program);
        }
        break;
      case CHAT_FORM_CONSTANTS.CONSENT:
        const orderConsentLocalStorage = sessionStorage.getItem(CHAT_FORM_CONSTANTS.ORDER);
        if(orderConsentLocalStorage){
          const orderSelectEvent = JSON.parse(orderConsentLocalStorage) ;
          this.onSelectProgram(orderSelectEvent.program);
        }
        break;
      default : return null;
    }
  }

  /**
   * Restricts input to numbers only
   * @param event - Keyboard event
   * @returns boolean indicating if input is valid
   */
  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /**
   * Restricts input to letters and spaces only
   * @param event - Keyboard event
   * @returns boolean indicating if input is valid
   */
  noSpecialChars(event: KeyboardEvent): boolean {
    // Allow only letters and spaces using Unicode
    const regex = /^[\p{L}\s]$/u;
    const char = String.fromCharCode(event.which || event.keyCode);
    
    if (!regex.test(char)) {
        event.preventDefault();
        return false;
    }
    return true;
  }

  /**
   * Handles success page load
   * Cleans up auth data and navigates to chatbot
   */
  onSuccessPageLoad(){
    localStorage.removeItem('patientAuth');
    setTimeout(() => {
      this.router.navigate(['/chatbot']);
    }, 5000);
  }

  public convertToUrlWithoutProtocol(text: string): string {
    /**
     * Create a regex without anchors for finding URLs within text
     */
    const urlRegex = PATTERN.URL_REGEX_WITHOUT_PROTOCOL;
    return text.replace(urlRegex, url => {
      /**
       *  Ensure URL has protocol for href 
       */
      const href = url.startsWith('http://') || url.startsWith('https://') || url.startsWith('ftp://') 
        ? url 
        : 'https://' + url;
      return '<a href="' + href + '" target="_blank">' + url + '</a>';
    });
  }
}
